// ─── CasaisDashboard — "Análise Global" (variante mercadoria) ───────────────
// Dashboard SÓ DE LEITURA (sem botões) da migração CasaisHub. Agrega as linhas
// de transporte das OTs (tipo + horas + valor) via GET /casais-import/stats:
// total de transportes, custo acumulado, evolução mensal, distribuição por tipo
// e Top 5 viaturas/obras. SAP fica como placeholder até haver estado de lançamento.
//
// TEMA: segue o tema escolhido na barra principal (menu do avatar) —
// plannerToolbarTheme ∈ {classic|light|dark}. Clássico e Claro usam a paleta
// clara; Escuro usa superfícies escuras. Tokens --dash-* por data-dash-theme
// (mesmo padrão dos tokens --tb-* do Header); as cores dos gráficos (recharts
// são props inline, não CSS) são calculadas no render a partir do tema.
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import TimelineIcon from '@material-ui/icons/Timeline';
import PieChartIcon from '@material-ui/icons/PieChart';
import { AuthContext } from '../../App';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
// Paleta categórica (donut) — distinta e acessível.
const TYPE_COLORS = ['#2563eb', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#0ea5e9', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

// Lê o tema atual da barra e reduz a dois modos de superfície (claro/escuro):
// Clássico (barra verde) mostra o corpo claro, tal como o Claro.
function readDashTheme() {
  try { return localStorage.getItem('plannerToolbarTheme') === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
}

const useStyles = makeStyles(() => ({
  '@global': {
    '[data-dash-theme="light"]': {
      '--dash-page': '#f8fafc',
      '--dash-card': '#ffffff',
      '--dash-border': '#eef2f7',
      '--dash-shadow': '0 1px 2px rgba(15,23,42,0.04)',
      '--dash-title': '#0f172a',
      '--dash-sub': '#64748b',
      '--dash-muted': '#94a3b8',
      '--dash-strong': '#0f172a',
      '--dash-rank-label': '#334155',
      '--dash-track': '#f1f5f9',
    },
    '[data-dash-theme="dark"]': {
      '--dash-page': '#0b1522',
      '--dash-card': '#122133',
      '--dash-border': 'rgba(110,193,228,0.14)',
      '--dash-shadow': '0 2px 12px rgba(0,0,0,0.45)',
      '--dash-title': '#eaf3fb',
      '--dash-sub': '#a9bed3',
      '--dash-muted': '#7e95ac',
      '--dash-strong': '#eaf3fb',
      '--dash-rank-label': '#c4d3e3',
      '--dash-track': 'rgba(255,255,255,0.08)',
    },
  },
  page: { background: 'var(--dash-page, #f8fafc)', minHeight: '100%', width: '100%', transition: 'background 0.25s ease' },
  root: { padding: 24, maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 },
  headerIcon: { width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  title: { fontSize: '1.6rem', fontWeight: 800, color: 'var(--dash-title, #0f172a)', letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.84rem', color: 'var(--dash-sub, #64748b)' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16, marginBottom: 22 },
  kpi: { background: 'var(--dash-card, #fff)', borderRadius: 16, padding: '18px 20px', border: '1px solid var(--dash-border, #eef2f7)', boxShadow: 'var(--dash-shadow, 0 1px 2px rgba(15,23,42,0.04))' },
  kpiTop: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  kpiIcon: { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiLabel: { fontSize: '0.64rem', fontWeight: 700, color: 'var(--dash-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  kpiNum: { fontSize: '2rem', fontWeight: 800, color: 'var(--dash-strong, #0f172a)', lineHeight: 1.1, letterSpacing: '-0.03em' },
  kpiHint: { fontSize: '0.72rem', color: 'var(--dash-muted, #94a3b8)', marginTop: 6 },
  row2: { display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16, marginBottom: 22, '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } },
  card: { background: 'var(--dash-card, #fff)', borderRadius: 16, padding: '18px 20px', border: '1px solid var(--dash-border, #eef2f7)', boxShadow: 'var(--dash-shadow, 0 1px 2px rgba(15,23,42,0.04))' },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.92rem', fontWeight: 800, color: 'var(--dash-title, #0f172a)', marginBottom: 14 },
  rankRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  rankLabel: { fontSize: '0.78rem', color: 'var(--dash-rank-label, #334155)', width: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 },
  rankBarWrap: { flex: 1, height: 10, background: 'var(--dash-track, #f1f5f9)', borderRadius: 999, overflow: 'hidden' },
  rankBar: { height: '100%', borderRadius: 999 },
  rankVal: { fontSize: '0.76rem', fontWeight: 700, color: 'var(--dash-strong, #0f172a)', width: 90, textAlign: 'right', flexShrink: 0 },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 },
  empty: { color: 'var(--dash-muted, #94a3b8)', fontSize: '0.86rem', textAlign: 'center', padding: 24 },
}));

function CasaisDashboard({ lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashTheme, setDashTheme] = useState(readDashTheme);

  // Segue o tema ao vivo: evento próprio (mesmo separador) + 'storage' (outros).
  useEffect(() => {
    const sync = () => setDashTheme(readDashTheme());
    window.addEventListener('plannerThemeChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('plannerThemeChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isDark = dashTheme === 'dark';
  // Cores dos gráficos (recharts usa props inline, não CSS) por tema.
  const chart = useMemo(() => ({
    grid: isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7',
    axis: isDark ? '#8ba3bb' : '#94a3b8',
    tooltipBg: isDark ? '#122133' : '#ffffff',
    tooltipBorder: isDark ? 'rgba(110,193,228,0.22)' : '#eef2f7',
    tooltipLabel: isDark ? '#eaf3fb' : '#0f172a',
  }), [isDark]);

  const eur = useMemo(() => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }), []);
  const eurShort = useCallback((v) => eur.format(Number(v) || 0), [eur]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch('/planner-api/casais-import/stats', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Box className={classes.page} data-dash-theme={dashTheme}>
        <Box className={classes.loading}><CircularProgress /></Box>
      </Box>
    );
  }

  const d = data || {};
  const monthly = (d.evolucaoMensal || []).map((v, i) => ({ mes: MONTHS[i], custo: v }));
  const porTipo = (d.porTipo || []).filter(t => t.custo > 0);
  const top5V = d.top5Viaturas || [];
  const top5O = d.top5Obras || [];
  const maxV = Math.max(1, ...top5V.map(x => x.custo));
  const maxO = Math.max(1, ...top5O.map(x => x.custo));

  const KPIS = [
    { key: 'tot', label: pt ? 'Total de Transportes' : 'Total transports', icon: LocalShippingIcon, tint: '#eaf2fe', color: '#2563eb', value: (d.totalTransportes ?? 0).toLocaleString('pt-PT'), hint: pt ? 'Registos contabilizados' : 'Records' },
    { key: 'cost', label: pt ? 'Custo Total Acumulado' : 'Total cost', icon: EuroSymbolIcon, tint: '#e9f7ec', color: '#10b981', value: eurShort(d.custoTotal), hint: pt ? 'Somatório financeiro' : 'Financial total' },
    { key: 'sap', label: pt ? 'Lançamento SAP' : 'SAP posting', icon: CloudDoneIcon, tint: '#eef2f7', color: '#64748b', value: d.sap?.lancados != null ? `${d.sap.lancados} / ${d.sap.total}` : '—', hint: pt ? 'Por configurar' : 'Not configured' },
  ];

  return (
    <Box className={classes.page} data-dash-theme={dashTheme}>
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}><TimelineIcon /></Box>
        <Box>
          <Typography className={classes.title}>{pt ? 'Análise Global' : 'Global Analysis'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Transportes, custos e distribuição' : 'Transports, costs and distribution'}{d.year ? ` · ${d.year}` : ''}</Typography>
        </Box>
      </Box>

      {/* KPIs */}
      <Box className={classes.kpiGrid}>
        {KPIS.map(k => (
          <Box key={k.key} className={classes.kpi}>
            <Box className={classes.kpiTop}>
              <Box className={classes.kpiIcon} style={{ background: k.tint }}><k.icon style={{ color: k.color, fontSize: 20 }} /></Box>
              <Typography className={classes.kpiLabel}>{k.label}</Typography>
            </Box>
            <Typography className={classes.kpiNum}>{k.value}</Typography>
            <Typography className={classes.kpiHint}>{k.hint}</Typography>
          </Box>
        ))}
      </Box>

      {/* Evolução mensal + Distribuição por tipo */}
      <Box className={classes.row2}>
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}><TimelineIcon style={{ fontSize: 18, color: '#2563eb' }} /> {pt ? 'Evolução Mensal de Custos (€)' : 'Monthly cost evolution (€)'}</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthly} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="casaisCost" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: chart.axis }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: chart.axis }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <Tooltip formatter={(v) => eurShort(v)} labelStyle={{ color: chart.tooltipLabel, fontWeight: 700 }} contentStyle={{ borderRadius: 10, background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipLabel, fontSize: 12 }} />
              <Area type="monotone" dataKey="custo" stroke="#2563eb" strokeWidth={2.4} fill="url(#casaisCost)" dot={{ r: 2.5, fill: '#2563eb' }} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        <Box className={classes.card}>
          <Typography className={classes.cardTitle}><PieChartIcon style={{ fontSize: 18, color: '#8b5cf6' }} /> {pt ? 'Distribuição por Tipo de Transporte' : 'By transport type'}</Typography>
          {porTipo.length === 0 ? (
            <Typography className={classes.empty}>{pt ? 'Sem custos por tipo ainda.' : 'No data yet.'}</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={porTipo} dataKey="custo" nameKey="label" cx="50%" cy="50%" innerRadius={62} outerRadius={96} paddingAngle={2}>
                  {porTipo.map((entry, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => eurShort(v)} contentStyle={{ borderRadius: 10, background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipLabel, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: chart.axis }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>

      {/* Top 5 viaturas + Top 5 obras */}
      <Box className={classes.row2} style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}><LocalShippingIcon style={{ fontSize: 18, color: '#2563eb' }} /> {pt ? 'Top 5 Viaturas por Custo (€)' : 'Top 5 vehicles by cost'}</Typography>
          {top5V.length === 0 ? <Typography className={classes.empty}>{pt ? 'Sem dados.' : 'No data.'}</Typography> : top5V.map((v, i) => (
            <Box key={i} className={classes.rankRow}>
              <Typography className={classes.rankLabel} title={v.label}>{v.label}</Typography>
              <Box className={classes.rankBarWrap}><Box className={classes.rankBar} style={{ width: `${Math.max(4, (v.custo / maxV) * 100)}%`, background: '#2563eb' }} /></Box>
              <Typography className={classes.rankVal}>{eurShort(v.custo)}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.card}>
          <Typography className={classes.cardTitle}><PieChartIcon style={{ fontSize: 18, color: '#f59e0b' }} /> {pt ? 'Top 5 Obras com Maior Despesa' : 'Top 5 works by spend'}</Typography>
          {top5O.length === 0 ? <Typography className={classes.empty}>{pt ? 'Sem dados.' : 'No data.'}</Typography> : top5O.map((o, i) => (
            <Box key={i} className={classes.rankRow}>
              <Typography className={classes.rankLabel} title={o.label}>{o.label}</Typography>
              <Box className={classes.rankBarWrap}><Box className={classes.rankBar} style={{ width: `${Math.max(4, (o.custo / maxO) * 100)}%`, background: '#f59e0b' }} /></Box>
              <Typography className={classes.rankVal}>{eurShort(o.custo)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    </Box>
  );
}

export default CasaisDashboard;
